import { stringify } from 'query-string'
import { toast } from '@/components/toasts/toast.helpers'
import {
  setConfigPaths,
  setConfigPathsLoading,
  updateConfigPaths,
} from '@/reducers/realtime/alerts/realtimeNewAlertConfiguration.redux'
import {
  setLoading,
  setAlertConfigurations,
} from '@/reducers/realtime/alerts/realtimeOrganizationAlertConfigurations.redux'
import { HYDRATE_CHAT_HISTORY } from '@/reducers/realtime/realtimeChats.redux'
import { CLEAR_ALERT_TRANSCRIPTION } from '@/reducers/realtime/alerts/realtimeUserAlerts.redux'
import { stopListeningToCall } from '@/reducers/commandCenter/commandCenter.redux'
import { fetchingAPI, apiService } from '@/api'
import { fetchOrganization } from '../organizations/organization.actions'
import { fetchOrganizationTags } from '../organizations/organizationTags.actions'
import { fetchOrganizationTagCategories } from '../organizations/organizationTagCategories.actions'
import { fetchUsers } from '../organizations/organizationUsers.actions'

export const startListeningToAlert =
  ({ agentUsername, alertCallId, processingUrl, managerUsername, alertId }) =>
  (dispatch) => {
    dispatch({
      type: 'realtime/attemptListeningToCall',
      agentUsername,
      alertCallId,
      processingUrl,
      managerUsername,
      alertId,
    })
  }

export const stopListeningToAlert = () => (dispatch) => {
  dispatch({ type: 'realtime/stopListeningToCall' })
}

// Switch the single live-listen to this agent's call: stop any current listen,
// clear its transcript, then start the new one. Shared by the bell's "Listen
// call" action and the in-chat Listen toggle so both behave identically.
export const startListeningToAgentAlert =
  ({ agentUsername, alertCallId, processingUrl, managerUsername, alertId }) =>
  (dispatch) => {
    dispatch(stopListeningToAlert())
    dispatch(stopListeningToCall())
    dispatch({ type: CLEAR_ALERT_TRANSCRIPTION })
    dispatch(
      startListeningToAlert({
        agentUsername,
        alertCallId,
        processingUrl,
        managerUsername,
        alertId,
      })
    )
  }

// Pass 2 mid-call join: resolve, at click time, whether a persisted bell
// notification's call is still live and hand back the current join context, so
// a manager who was offline when it fired (or who reloaded) can Listen/Chat.
// Returns { isLive, agentId, agentFirstName, agentLastName, processingUrl,
// callId } or null on error. Coverage is decided by the caller (alertCoverage)
// and enforced by the RTC socket on the actual listen/chat.
export const resolveLiveCoaching =
  ({ callId, agentUsername }) =>
  async (dispatch) => {
    if (!callId || !agentUsername) {
      return null
    }
    const url = `${apiService.rtc}/live_coaching/resolve?${stringify({
      call_id: callId,
      agent_username: agentUsername,
    })}`
    try {
      return await fetchingAPI(url, 'GET', dispatch)
    } catch (err) {
      // Cold-join is best-effort — a failure just leaves the row without live
      // actions (same as "not live"), never a stale join.
      return null
    }
  }

export const fetchChatHistory =
  ({ agentUsername, before = null, limit = 50 }) =>
  async (dispatch) => {
    if (!agentUsername) {
      return false
    }
    dispatch({
      type: 'realtime/setChatHistoryLoading',
      agentUsername,
      historyLoading: true,
    })
    const params = { agent_username: agentUsername, limit }
    if (before) {
      params.before = before
    }
    const url = `${apiService.rtc}/chats?${stringify(params)}`
    try {
      const response = await fetchingAPI(url, 'GET', dispatch)
      const messages = response?.messages || []
      dispatch({
        type: HYDRATE_CHAT_HISTORY,
        agentUsername,
        messages,
        // A short page means we've reached the end of the conversation —
        // the scroll-to-top handler uses this to stop fetching.
        hasMore: messages.length === limit,
        // First page has no `before` cursor — used by the component to know
        // when to snap the scroll position to the bottom.
        isInitial: !before,
      })

      return true
    } catch (err) {
      toast.error('Failed to load chat history', { cause: err })

      // The caller guards against re-requesting the same cursor; tell it this
      // page never arrived so the user can retry by scrolling up again.
      return false
    } finally {
      dispatch({
        type: 'realtime/setChatHistoryLoading',
        agentUsername,
        historyLoading: false,
      })
    }
  }

export const closeAlert =
  ({ username }) =>
  (dispatch, getState) => {
    const remainingAlerts = { ...getState().realtimeUserAlerts.alerts }

    delete remainingAlerts[username]

    if (getState()?.realtimeUserAlerts?.listeningToUsername === username) {
      dispatch(stopListeningToAlert())
    }
    dispatch({ type: 'realtime/dismissAlert', alerts: remainingAlerts })
  }

export const loadPlaybookNamesAndCids = (
  { userOrganizationId = null } = { userOrganizationId: null }
) => {
  return async (dispatch, getState) => {
    dispatch(setConfigPathsLoading(true))

    try {
      const organizationId = userOrganizationId || getState().currentUser.organizationid
      const requestedPropertiesQueryString = `?${stringify({
        requested_properties: 'name,id,cid',
        active_only: true,
      })}`
      const configUrl = `${apiService.core}/api/${organizationId}/configs${requestedPropertiesQueryString}`
      const configs = await fetchingAPI(configUrl, 'GET', dispatch)
      const configPaths = {}

      configs.forEach((config) => {
        if (!configPaths[config.cid]) {
          configPaths[config.cid] = {
            name: config.name,
            id: config.id,
            paths: [],
          }
        }
      })

      dispatch(setConfigPaths(configPaths))
    } catch (err) {
      toast.error('Failed to load configPaths', { cause: err })
    } finally {
      dispatch(setConfigPathsLoading(false))
    }
  }
}

const buildPaths = (type, config, configPaths) => {
  if (!config.body) {
    return
  }

  const entries = config.body[type]?.entries

  if (entries) {
    Object.keys(entries).forEach((entry) => {
      const categoryName = entries[entry].name

      if (entries[entry]?.entries && ['deck', 'classified_postcall'].includes(type)) {
        const nestedEntry = entries[entry].entries

        if (Object.keys(nestedEntry).length > 0) {
          const nestedOptions = []

          Object.keys(nestedEntry).forEach((insideEntry) => {
            if (nestedEntry[insideEntry]) {
              const formattedName = nestedEntry[insideEntry].name
              const formattedValue = `${type} - ${categoryName} - ${formattedName}`

              if (!nestedOptions.filter((i) => i.value === formattedValue).length) {
                nestedOptions.push({
                  label: formattedName,
                  value: formattedValue,
                  key: formattedName,
                })
              }
            }
          })

          configPaths[config.cid].paths.push({
            label: categoryName,
            options: nestedOptions,
            type,
          })
        }
      } else {
        const formattedValue = `${type} - ${categoryName}`
        if (configPaths[config.cid].paths.filter((i) => i.value === formattedValue).length === 0) {
          const item = {
            label: categoryName,
            value: formattedValue,
            key: categoryName,
            type,
          }

          if (type === 'checklist' && entries[entry].trigger?.required_before) {
            item.required_before = entries[entry].trigger.required_before
          }
          configPaths[config.cid].paths.push(item)
        }
      }
    })
  }
}

export const loadPlaybookBodyByCid = ({ playbookCid = '' }) => {
  return async (dispatch) => {
    dispatch(setConfigPathsLoading(true))

    try {
      const configUrl = `${apiService.core}/api/config/${playbookCid}`
      const { configs } = await fetchingAPI(configUrl, 'GET', dispatch)
      const config = configs[0]
      const configPaths = {}

      configPaths[config.cid] = {
        name: config.name,
        paths: [],
      }

      const configTypes = ['deck', 'checklist', 'notifications', 'classified_postcall']
      configTypes.forEach((path) => {
        buildPaths(path, config, configPaths)
      })

      dispatch(updateConfigPaths(configPaths))
    } catch (err) {
      toast.error('Failed to load playbook', { cause: err })
    } finally {
      dispatch(setConfigPathsLoading(false))
    }
  }
}

export const loadAlertConfigurations = (
  { ignoreLoading = false, userId = null, userOrganizationId = null } = {
    ignoreLoading: false,
    userId: null,
    userOrganizationId: null,
  }
) => {
  return async (dispatch, getState) => {
    if (!ignoreLoading) {
      dispatch(setLoading(true))
    }

    try {
      const organizationId = userOrganizationId || getState().currentUser.organizationid
      const formattedQueryString = userId ? `?${stringify({ user_id: userId })}` : ''
      const url = `${apiService.core}/api/alert_configs${formattedQueryString}`
      const [alert_configurations] = await Promise.all([
        fetchingAPI(url, 'GET', dispatch),
        fetchOrganization({ organizationId })(dispatch),
        fetchOrganizationTagCategories({ organizationId })(dispatch),
        fetchOrganizationTags({ organizationId })(dispatch),
        fetchUsers({ organizationId })(dispatch),
      ])

      dispatch(setAlertConfigurations(alert_configurations))
    } catch (err) {
      toast.error('Failed to load alerts', { cause: err })
    } finally {
      if (!ignoreLoading) {
        dispatch(setLoading(false))
      }
    }
  }
}

export const editAlertConfiguration =
  ({ data }, toastMessage = null) =>
  async (dispatch, getState) => {
    dispatch(setLoading(true))
    const url = `${apiService.core}/api/alert_config`
    try {
      await fetchingAPI(url, 'POST', dispatch, JSON.stringify(data))
      dispatch({
        type: 'REFRESH_USERS_ALERTS',
      })
      await loadAlertConfigurations({ ignoreLoading: true })(dispatch, getState)
    } catch (err) {
      toast.error('Failed to edit alert', { cause: err })
    } finally {
      dispatch(setLoading(false))
      if (toastMessage) {
        toast.success(toastMessage)
      }
    }
  }

export const deleteAlertConfiguration =
  ({ alertConfigurationId, alertName }) =>
  async (dispatch, getState) => {
    dispatch(setLoading(true))
    const alertConfigs = getState().realtimeOrganizationAlertConfigurations.alertConfigurations
    try {
      await fetchingAPI(
        `${apiService.core}/api/alert_config/${alertConfigurationId}`,
        'DELETE',
        dispatch
      )

      dispatch(
        setAlertConfigurations(
          alertConfigs.filter((item) => {
            return item.id !== alertConfigurationId
          })
        )
      )

      dispatch({
        type: 'REFRESH_USERS_ALERTS',
      })
    } catch (err) {
      toast.error('Failed to delete alert', { cause: err })
    } finally {
      dispatch(setLoading(false))
      toast.success(`${alertName} has been deleted`)
    }
  }
